import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Statistic,
  Tag,
  Progress,
  Space,
  Typography,
  Alert,
  Badge,
  Tabs,
  DatePicker,
  Select,
  Button,
  Spin,
  message,
} from "antd";
import {
  ShoppingOutlined,
  DollarOutlined,
  BarChartOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ChartCard from "../../charts/ChartCard";
import { getsalidas } from "../../../services/entregas/servicesEntregas";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const InformeEntregas = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");

  // Componente para la tabla de productos PO
  const ProductPOTable = ({ filteredDeliveries }) => {
    const productPOData = useMemo(() => {
      const productMap = {};

      filteredDeliveries.forEach((delivery) => {
        const projectName = delivery.jsonDetalleSalida.proyecto.sigla;
        const projectCode = delivery.jsonDetalleSalida.proyecto.codigo_interno;
        const deliveryDate = delivery.fecha_salida;
        const actaCode = delivery.codActa;

        delivery.jsonDetalleSalida.entrega.items.forEach((item) => {
          const key = `${item.po_producto_oid}-${item.orden_compra.numero_orden}`;

          if (!productMap[key]) {
            productMap[key] = {
              po_producto_oid: item.po_producto_oid,
              numero_orden: item.orden_compra.numero_orden,
              fecha_po: item.orden_compra.fecha_po,
              proveedor: item.orden_compra.proveedor,
              descripcion_po: item.descripcion_po,
              unidad_po: item.unidad_po,
              valor_unitario: item.valor_unitario,
              cantidad_po: item.cantidad_po,
              cantidad_entrada_lote: item.lote.cantidad_entrada,
              total_asignado: 0,
              total_entregado: 0,
              entregas: [],
              productos_plan: new Set(),
              seriales_utilizados: new Set(),
            };
          }

          // Sumar asignaciones
          item.asignaciones.forEach((asig) => {
            productMap[key].total_asignado += asig.cantidad_asignada;
            productMap[key].total_entregado += asig.cantidad_asignada; // En este contexto, asignado = entregado
            productMap[key].productos_plan.add(asig.producto_plan.nombre);

            // Agregar seriales de manera segura
            if (Array.isArray(asig.seriales) && asig.seriales.length > 0) {
              asig.seriales.forEach((serial) => {
                productMap[key].seriales_utilizados.add(serial);
              });
            }

            // Agregar información de entrega
            productMap[key].entregas.push({
              fecha: deliveryDate,
              proyecto: projectName,
              codigo_proyecto: projectCode,
              acta: actaCode,
              cantidad: asig.cantidad_asignada,
              producto_plan: asig.producto_plan.nombre,
              seriales: asig.seriales,
            });
          });
        });
      });

      // Convertir a array y calcular porcentajes
      return Object.values(productMap).map((item, index) => ({
        ...item,
        key: index,
        productos_plan: Array.from(item.productos_plan),
        seriales_utilizados: Array.from(item.seriales_utilizados),
        porcentaje_entregado: (item.total_entregado / item.cantidad_po) * 100,
        valor_total_po: item.cantidad_po * item.valor_unitario,
        valor_total_entregado: item.total_entregado * item.valor_unitario,
        pendiente_entregar: item.cantidad_po - item.total_entregado,
      }));
    }, [filteredDeliveries]);

    const expandedRowRender = (record) => {
      const entregasColumns = [
        {
          title: "Fecha Entrega",
          dataIndex: "fecha",
          key: "fecha",
          width: 120,
        },
        {
          title: "Proyecto",
          dataIndex: "proyecto",
          key: "proyecto",
          render: (text, row) => (
            <Space direction="vertical" size={0}>
              <Text strong>{text}</Text>
              <Tag color="blue" style={{ fontSize: "10px" }}>
                {row.codigo_proyecto}
              </Tag>
            </Space>
          ),
        },
        {
          title: "Acta",
          dataIndex: "acta",
          key: "acta",
          render: (text) => <Tag color="orange">{text}</Tag>,
        },
        {
          title: "Cantidad Entregada",
          dataIndex: "cantidad",
          key: "cantidad",
          align: "center",
          render: (value) => (
            <Badge count={value} style={{ backgroundColor: "#52c41a" }} />
          ),
        },
        {
          title: "Producto Plan",
          dataIndex: "producto_plan",
          key: "producto_plan",
          width: 250,
        },
        {
          title: "Seriales",
          dataIndex: "seriales",
          key: "seriales",
          render: (seriales) => (
            <Space wrap>
              {seriales.map((serial, idx) => (
                <Tag key={idx} color="processing" style={{ fontSize: "10px" }}>
                  {serial}
                </Tag>
              ))}
            </Space>
          ),
        },
      ];

      return (
        <div style={{ margin: "16px 0" }}>
          <Title level={5}>Historial de Entregas</Title>
          <Table
            columns={entregasColumns}
            dataSource={record.entregas.map((entrega, idx) => ({
              ...entrega,
              key: idx,
            }))}
            size="small"
            bordered
          />
        </div>
      );
    };

    const productPOColumns = [
      {
        title: "PO OID",
        dataIndex: "po_producto_oid",
        key: "po_producto_oid",
        width: 80,
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: "Orden Compra",
        dataIndex: "numero_orden",
        key: "numero_orden",
        width: 120,
        render: (text, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{text}</Text>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              {record.fecha_po}
            </Text>
          </Space>
        ),
      },
      {
        title: "Descripción PO",
        dataIndex: "descripcion_po",
        key: "descripcion_po",
        width: 150,
        render: (text) => <Text>{text}</Text>,
      },
      {
        title: "Proveedor",
        dataIndex: "proveedor",
        key: "proveedor",
        width: 180,
        render: (text) => <Text style={{ fontSize: "12px" }}>{text}</Text>,
      },
      {
        title: "Cantidad PO",
        dataIndex: "cantidad_po",
        key: "cantidad_po",
        align: "center",
        width: 100,
        render: (value, record) => (
          <Space direction="vertical" size={0} style={{ textAlign: "center" }}>
            <Badge count={value} style={{ backgroundColor: "#1890ff" }} />
            <Text type="secondary" style={{ fontSize: "10px" }}>
              {record.unidad_po}
            </Text>
          </Space>
        ),
      },
      {
        title: "Cantidad Lote",
        dataIndex: "cantidad_entrada_lote",
        key: "cantidad_entrada_lote",
        align: "center",
        width: 100,
        render: (value) => (
          <Badge count={value} style={{ backgroundColor: "#722ed1" }} />
        ),
      },
      {
        title: "Total Asignado",
        dataIndex: "total_asignado",
        key: "total_asignado",
        align: "center",
        width: 100,
        render: (value) => (
          <Badge count={value} style={{ backgroundColor: "#f56a00" }} />
        ),
      },
      {
        title: "Total Entregado",
        dataIndex: "total_entregado",
        key: "total_entregado",
        align: "center",
        width: 100,
        render: (value) => (
          <Badge count={value} style={{ backgroundColor: "#52c41a" }} />
        ),
      },
      {
        title: "Pendiente",
        dataIndex: "pendiente_entregar",
        key: "pendiente_entregar",
        align: "center",
        width: 80,
        render: (value) => (
          <Badge
            count={value}
            style={{
              backgroundColor: value > 0 ? "#ff4d4f" : "#52c41a",
            }}
          />
        ),
      },
      {
        title: "% Completado",
        dataIndex: "porcentaje_entregado",
        key: "porcentaje_entregado",
        align: "center",
        width: 120,
        render: (value) => (
          <Progress
            percent={value}
            size="small"
            strokeColor={
              value === 100 ? "#52c41a" : value >= 50 ? "#1890ff" : "#ff4d4f"
            }
            format={(percent) => `${percent.toFixed(1)}%`}
          />
        ),
      },
      {
        title: "Valor Unit.",
        dataIndex: "valor_unitario",
        key: "valor_unitario",
        align: "right",
        width: 100,
        render: (value) => <Text>${value.toLocaleString()}</Text>,
      },
      {
        title: "Valor Total PO",
        dataIndex: "valor_total_po",
        key: "valor_total_po",
        align: "right",
        width: 120,
        render: (value) => (
          <Text strong style={{ color: "#1890ff" }}>
            ${value.toLocaleString()}
          </Text>
        ),
      },
      {
        title: "Valor Entregado",
        dataIndex: "valor_total_entregado",
        key: "valor_total_entregado",
        align: "right",
        width: 120,
        render: (value) => (
          <Text strong style={{ color: "#52c41a" }}>
            ${value.toLocaleString()}
          </Text>
        ),
      },
    ];

    const summaryData = useMemo(() => {
      const totalPO = productPOData.reduce(
        (sum, item) => sum + item.valor_total_po,
        0
      );
      const totalEntregado = productPOData.reduce(
        (sum, item) => sum + item.valor_total_entregado,
        0
      );
      const totalProductos = productPOData.length;
      const productosCompletos = productPOData.filter(
        (item) => item.porcentaje_entregado === 100
      ).length;

      return { totalPO, totalEntregado, totalProductos, productosCompletos };
    }, [productPOData]);

    return (
      <div>
        {/* Resumen de PO */}
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Productos PO"
                value={summaryData.totalProductos}
                prefix={<ShoppingOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Completados"
                value={summaryData.productosCompletos}
                suffix={`/ ${summaryData.totalProductos}`}
                prefix={<Badge status="success" />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Valor Total PO"
                value={summaryData.totalPO}
                formatter={(value) => `${value.toLocaleString()}`}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Valor Entregado"
                value={summaryData.totalEntregado}
                formatter={(value) => `${value.toLocaleString()}`}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Detalle de Productos por Orden de Compra">
          <Table
            dataSource={productPOData}
            columns={productPOColumns}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => record.entregas.length > 0,
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} productos PO`,
            }}
            scroll={{ x: 1500 }}
            size="small"
            bordered
          />
        </Card>
      </div>
    );
  };

  // Función para obtener los datos del servicio
  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const result = await getsalidas();

      if (result.status === 200) {
        setDeliveries(result.data);
        setFilteredDeliveries(result.data);
        message.success("Datos cargados correctamente");
      } else {
        message.error(result.message || "Error al cargar los datos");
      }
    } catch (error) {
      console.error("Error fetching deliveries:", error);
      message.error("Error al conectar con el servicio");
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchDeliveries();
  }, []);

  // Filtrar entregas basado en fechas y proyecto seleccionado
  useEffect(() => {
    let filtered = deliveries;

    // Filtro por fechas
    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter((delivery) => {
        const deliveryDate = dayjs(delivery.fecha_salida, "DD/MM/YYYY");
        return (
          deliveryDate.isAfter(dateRange[0]) &&
          deliveryDate.isBefore(dateRange[1])
        );
      });
    }

    // Filtro por proyecto
    if (selectedProject !== "all") {
      filtered = filtered.filter(
        (delivery) =>
          delivery.jsonDetalleSalida.proyecto.proyecto_oid ===
          parseInt(selectedProject)
      );
    }

    setFilteredDeliveries(filtered);
  }, [deliveries, dateRange, selectedProject]);

  // Estadísticas generales
  const generalStats = useMemo(() => {
    const totalValue = filteredDeliveries.reduce(
      (sum, delivery) => sum + delivery.valor_total,
      0
    );
    const totalDeliveries = filteredDeliveries.length;
    const totalItems = filteredDeliveries.reduce((sum, delivery) => {
      return (
        sum +
        delivery.jsonDetalleSalida.entrega.items.reduce((itemSum, item) => {
          return (
            itemSum +
            item.asignaciones.reduce(
              (asigSum, asig) => asigSum + asig.cantidad_asignada,
              0
            )
          );
        }, 0)
      );
    }, 0);

    const uniqueProjects = new Set(
      filteredDeliveries.map((d) => d.jsonDetalleSalida.proyecto.proyecto_oid)
    ).size;

    return {
      totalValue,
      totalDeliveries,
      totalItems,
      uniqueProjects,
    };
  }, [filteredDeliveries]);

  // Datos para gráfico de entregas por proyecto
  const projectDeliveryData = useMemo(() => {
    const projectMap = {};

    filteredDeliveries.forEach((delivery) => {
      const projectName = delivery.jsonDetalleSalida.proyecto.sigla;
      if (!projectMap[projectName]) {
        projectMap[projectName] = {
          count: 0,
          value: 0,
        };
      }
      projectMap[projectName].count += 1;
      projectMap[projectName].value += delivery.valor_total;
    });

    return {
      labels: Object.keys(projectMap),
      datasets: [
        {
          label: "Número de Entregas",
          data: Object.values(projectMap).map((p) => p.count),
          backgroundColor: [
            "rgba(54, 162, 235, 0.8)",
            "rgba(255, 99, 132, 0.8)",
            "rgba(255, 206, 86, 0.8)",
            "rgba(75, 192, 192, 0.8)",
            "rgba(153, 102, 255, 0.8)",
            "rgba(255, 159, 64, 0.8)",
          ],
        },
      ],
    };
  }, [filteredDeliveries]);

  // Datos para gráfico de valor por proyecto
  const projectValueData = useMemo(() => {
    const projectMap = {};

    filteredDeliveries.forEach((delivery) => {
      const projectName = delivery.jsonDetalleSalida.proyecto.sigla;
      if (!projectMap[projectName]) {
        projectMap[projectName] = 0;
      }
      projectMap[projectName] += delivery.valor_total;
    });

    return {
      labels: Object.keys(projectMap),
      datasets: [
        {
          label: "Valor Total ($)",
          data: Object.values(projectMap),
          backgroundColor: [
            "rgba(255, 99, 132, 0.8)",
            "rgba(54, 162, 235, 0.8)",
            "rgba(255, 206, 86, 0.8)",
            "rgba(75, 192, 192, 0.8)",
            "rgba(153, 102, 255, 0.8)",
            "rgba(255, 159, 64, 0.8)",
          ],
        },
      ],
    };
  }, [filteredDeliveries]);

  // Datos para gráfico de entregas por mes
  const monthlyDeliveryData = useMemo(() => {
    const monthMap = {};

    filteredDeliveries.forEach((delivery) => {
      const date = dayjs(delivery.fecha_salida, "DD/MM/YYYY");
      const monthKey = date.format("YYYY-MM");
      const monthLabel = date.format("MMM YYYY");

      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          label: monthLabel,
          count: 0,
          value: 0,
        };
      }
      monthMap[monthKey].count += 1;
      monthMap[monthKey].value += delivery.valor_total;
    });

    const sortedEntries = Object.entries(monthMap).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    return {
      labels: sortedEntries.map(([_, data]) => data.label),
      datasets: [
        {
          label: "Entregas",
          data: sortedEntries.map(([_, data]) => data.count),
          backgroundColor: "rgba(54, 162, 235, 0.8)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 2,
        },
      ],
    };
  }, [filteredDeliveries]);

  // Datos para gráfico de productos más entregados
  const topProductsData = useMemo(() => {
    const productMap = {};

    filteredDeliveries.forEach((delivery) => {
      delivery.jsonDetalleSalida.entrega.items.forEach((item) => {
        item.asignaciones.forEach((asig) => {
          const productName = asig.producto_plan.nombre;
          if (!productMap[productName]) {
            productMap[productName] = 0;
          }
          productMap[productName] += asig.cantidad_asignada;
        });
      });
    });

    // Ordenar y tomar los top 10
    const sortedProducts = Object.entries(productMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    return {
      labels: sortedProducts.map(([name]) =>
        name.length > 30 ? name.substring(0, 30) + "..." : name
      ),
      datasets: [
        {
          label: "Cantidad Entregada",
          data: sortedProducts.map(([, count]) => count),
          backgroundColor: "rgba(75, 192, 192, 0.8)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 2,
        },
      ],
    };
  }, [filteredDeliveries]);

  // Tabla de entregas
  const deliveryTableData = useMemo(() => {
    return filteredDeliveries.map((delivery, index) => ({
      key: index,
      oid: delivery.oid,
      fecha_salida: delivery.fecha_salida,
      proyecto: delivery.jsonDetalleSalida.proyecto.sigla,
      codigo_interno: delivery.jsonDetalleSalida.proyecto.codigo_interno,
      valor_total: delivery.valor_total,
      codActa: delivery.codActa,
      responsable: delivery.oidResponsable,
      items_count: delivery.jsonDetalleSalida.entrega.items.length,
      participantes_count:
        delivery.jsonDetalleSalida.entrega.lista_participantes.length,
    }));
  }, [filteredDeliveries]);

  const tableColumns = [
    {
      title: "OID",
      dataIndex: "oid",
      key: "oid",
      width: 80,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Fecha Salida",
      dataIndex: "fecha_salida",
      key: "fecha_salida",
      width: 120,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Proyecto",
      dataIndex: "proyecto",
      key: "proyecto",
      width: 200,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Código",
      dataIndex: "codigo_interno",
      key: "codigo_interno",
      width: 120,
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Valor Total",
      dataIndex: "valor_total",
      key: "valor_total",
      width: 120,
      align: "right",
      render: (value) => (
        <Text strong style={{ color: "#1890ff" }}>
          ${value.toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Acta",
      dataIndex: "codActa",
      key: "codActa",
      width: 120,
      render: (text) => <Tag color="orange">{text}</Tag>,
    },
    {
      title: "Items",
      dataIndex: "items_count",
      key: "items_count",
      width: 80,
      align: "center",
    },
    {
      title: "Participantes",
      dataIndex: "participantes_count",
      key: "participantes_count",
      width: 100,
      align: "center",
    },
  ];

  // Lista única de proyectos para el filtro
  const uniqueProjects = useMemo(() => {
    const projects = deliveries.map((d) => ({
      oid: d.jsonDetalleSalida.proyecto.proyecto_oid,
      name: d.jsonDetalleSalida.proyecto.sigla,
    }));

    return projects.filter(
      (project, index, self) =>
        index === self.findIndex((p) => p.oid === project.oid)
    );
  }, [deliveries]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
      }}
    >
      <Title level={2} style={{ marginBottom: "24px", textAlign: "center" }}>
        <FileTextOutlined style={{ marginRight: "8px" }} />
        Dashboard de Entregas de Productos
      </Title>

      {/* Controles de Filtro */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Space>
              <Text strong>Filtrar por fecha:</Text>
              <RangePicker
                onChange={setDateRange}
                format="DD/MM/YYYY"
                placeholder={["Fecha inicio", "Fecha fin"]}
              />
            </Space>
          </Col>
          <Col xs={24} sm={8}>
            <Space>
              <Text strong>Proyecto:</Text>
              <Select
                value={selectedProject}
                onChange={setSelectedProject}
                style={{ width: 200 }}
                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
              >
                <Option value="all">Todos los proyectos</Option>
                {uniqueProjects.map((project) => (
                  <Option key={project.oid} value={project.oid.toString()}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={8}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchDeliveries}
            >
              Actualizar Datos
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Estadísticas Principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Entregas"
              value={generalStats.totalDeliveries}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Valor Total"
              value={generalStats.totalValue}
              prefix={<DollarOutlined />}
              precision={0}
              formatter={(value) => `$${value.toLocaleString()}`}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Items Entregados"
              value={generalStats.totalItems}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Proyectos Activos"
              value={generalStats.uniqueProjects}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: "#eb2f96" }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="1" size="large">
        <TabPane tab="Dashboard Gráfico" key="1" icon={<BarChartOutlined />}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <ChartCard
                title="Entregas por Proyecto"
                data={projectDeliveryData}
                chartType="pie"
              />
            </Col>
            <Col xs={24} lg={12}>
              <ChartCard
                title="Valor por Proyecto ($)"
                data={projectValueData}
                chartType="bar"
              />
            </Col>
            <Col xs={24} lg={12}>
              <ChartCard
                title="Entregas por Mes"
                data={monthlyDeliveryData}
                chartType="bar"
              />
            </Col>
            <Col xs={24} lg={12}>
              <ChartCard
                title="Top 10 Productos Más Entregados"
                data={topProductsData}
                chartType="bar"
              />
            </Col>

            <ProductPOTable filteredDeliveries={filteredDeliveries} />
          </Row>
        </TabPane>

        <TabPane tab="Tabla de Entregas" key="2" icon={<FileTextOutlined />}>
          <Card title="Registro Completo de Entregas">
            <Table
              dataSource={deliveryTableData}
              columns={tableColumns}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} entregas`,
              }}
              scroll={{ x: 1000 }}
              size="middle"
            />
          </Card>
        </TabPane>

        <TabPane tab="Análisis Detallado" key="3" icon={<ShoppingOutlined />}>
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Alert
                message="Resumen Ejecutivo"
                description={`Se han procesado ${
                  generalStats.totalDeliveries
                } entregas por un valor total de $${generalStats.totalValue.toLocaleString()}, 
                abarcando ${
                  generalStats.uniqueProjects
                } proyectos diferentes con un total de ${
                  generalStats.totalItems
                } items entregados.`}
                type="info"
                showIcon
                style={{ marginBottom: "16px" }}
              />
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default InformeEntregas;
